import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  exercisesLabel,
  parseExerciseCount,
  EXERCISE_COUNT_PRESETS,
  EXERCISE_COUNT_MIN,
  EXERCISE_COUNT_MAX,
} from "@/lib/students";

type Props = {
  /** What is typed, or null for "untouched — use this learner's own length". */
  draft: string | null;
  onDraftChange: (next: string | null) => void;
  /** This learner's own session length, shown when nothing is typed. */
  suggested: number;
  id?: string;
  label?: string;
  disabled?: boolean;
};

/**
 * How many questions a session should hold.
 *
 * A free number, not a list: the round lengths are offered as one tap because
 * they are the usual answer, but a grown-up wanting seven questions or forty
 * should not have to settle for the nearest number the app happens to offer.
 * Both places a session length is chosen — creating one and generating it —
 * share this, so the same number reads and clamps the same way in both.
 */
export function ExerciseCountField({
  draft,
  onDraftChange,
  suggested,
  id = "exercise-count",
  label = "Užduočių skaičius",
  disabled,
}: Props) {
  const text = draft ?? String(suggested);
  const requested = parseExerciseCount(text);
  const presets = useMemo(
    () => [...new Set([...EXERCISE_COUNT_PRESETS, suggested])].sort((a, b) => a - b),
    [suggested]
  );

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={EXERCISE_COUNT_MIN}
          max={EXERCISE_COUNT_MAX}
          className="w-24"
          value={text}
          disabled={disabled}
          onChange={(event) => onDraftChange(event.target.value)}
          // On the way out the field shows what will actually be asked for: an
          // out-of-range number snaps into range, and an empty one hands the
          // choice back to the learner's own session length.
          onBlur={() => onDraftChange(requested == null ? null : String(requested))}
        />
        {presets.map((preset) => (
          <Button
            key={preset}
            type="button"
            size="sm"
            variant={requested === preset ? "default" : "outline"}
            className="h-8 w-9 px-0 text-xs"
            disabled={disabled}
            onClick={() => onDraftChange(String(preset))}
          >
            {preset}
          </Button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Bet koks skaičius nuo {EXERCISE_COUNT_MIN} iki {EXERCISE_COUNT_MAX}. Tuščias laukas —{" "}
        {exercisesLabel(suggested)}, kaip įprasta šiam mokiniui.
      </p>
    </div>
  );
}
